"use client"

import { useState, useContext, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import SEO from "../_components/seo"

import clientPortals from "../../../assets/images/Client-Portal.png"
import featureCommunication from "../../../assets/images/featureCommunication.png"
import featureInboxOne from "../../../assets/images/feature-inbox 1.png"
import featurePayment from "../../../assets/images/feature-payments.png"
import featureScheduling from "../../../assets/images/feature-scheduling.webp"
import zoeeLogoOutline from "../../../assets/images/zoee-logo-outline-square.webp"
import ZoeeProfilePreview from "../../../assets/images/Profile.png"
import VideoEmbed from "../../../components/marketing/landing/VideoEmbed"

import { Button } from "../../../components/ui/button"
import { motion, useAnimation } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { useAuthModal } from "../../../components/auth/use-auth-modal"
import ClientNotification from "../../../components/client/ClientNotification"
import { Switch } from "../../../components/ui/switch"
import { SvgLightning } from "../../../components/icons"
import { UserCtx } from "../../../context/userContext"

export default function FeaturesPage() {
  const { showRegister } = useAuthModal()

  const controls = useAnimation()

  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  useEffect(() => {
    if (inView) {
      controls.start({ opacity: 1, scale: 1 })
    } else {
      controls.start({ opacity: 0, scale: 0.8 })
    }
  }, [inView, controls])

  return (
    <>
      <SEO
        title="Zoee | The All-In-One Coaching Platform Features"
        description="Zoee is an all-in-one coaching platform featuring everything you need to streamline your business and automate tasks, allowing you to focus on your clients."
      />
      <div className="overflow-hidden">
        <section className="flex min-h-[calc(100vh-100px)] items-center bg-gradient-to-t from-[#BACDFF] to-white pb-12 pt-48">
          <div className="container grid items-center gap-12 md:grid-cols-2">
            <div className="flex flex-col items-center gap-4 md:items-start lg:gap-8">
              <h1 className="max-w-screen-sm text-balance text-center text-2xl font-bold text-black md:text-left md:text-4xl lg:text-5xl">
                Built for coaches, by coaches.
              </h1>

              <p className="text-balance text-center text-base font-semibold md:text-left md:text-xl lg:text-3xl">
                We designed the Zoee coaching platform with you, the coach, in
                mind providing features to make running your business easier.
              </p>
              <div className="flex flex-wrap justify-center gap-2 md:justify-start">
                <Button
                  onClick={() => showRegister({ accountType: "coach" })}
                  variant="default"
                  size="lg"
                >
                  Start Your Free Trial
                </Button>
              </div>
            </div>

            <motion.div
              ref={inViewRef}
              className="w-full rounded-[20px] bg-black p-4"
              animate={controls}
            >
              <VideoEmbed />
            </motion.div>
          </div>
        </section>

        {/* Public Profile Page */}
        <section
          className="container my-24 grid items-center gap-12 md:my-12 md:grid-cols-2"
          id="public-profile"
        >
          <div className="flex flex-col items-center gap-4 md:items-start">
            <h2 className="text-balance text-center text-[24px] font-bold text-black md:text-left md:text-[36px] lg:text-[42px]">
              Personalized Zoee Website
            </h2>
            <p className="text-center text-sm md:text-left md:text-base lg:text-lg">
              Promote your brand on Zoee's customizable website where you can
              share your expertise, tailored client offerings (including
              individual and group programs), generate new bookings, add video
              and blog content, and seamlessly integrate all of your back-end
              business admin.
            </p>
            <a
              href="https://zoee.com/providers/lisa-morris-demo"
              className="text-blurple text-center font-bold"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Demo Website
            </a>
          </div>

          <img
            className="mx-auto md:-order-1 md:ml-0 md:mr-auto"
            src={ZoeeProfilePreview}
            alt="Coach profile page on Zoee, the all in one coaching platform."
          />
        </section>

        {/* Automatic Billing + Payments */}
        <section
          className="container my-36 grid items-center gap-12 md:my-24 md:grid-cols-2"
          id="automatic-billing-and-payments"
        >
          <div className="flex flex-col items-center gap-4 md:items-start">
            <h2 className="text-balance text-center text-[24px] font-bold text-black md:text-left md:text-[36px] lg:text-[42px]">
              Automatic Billing + Payments
            </h2>
            <p className="text-center text-sm md:text-left md:text-base lg:text-lg">
              Once your clients select a program and book their first session
              with you, we'll take the reins. Zoee is the all-in-one coaching
              platform that eliminates the need for you to manually handle
              client invoices, payments, cancellations, and tracking all of your
              hours and financial transactions.
            </p>
          </div>

          <img
            className="mx-auto md:ml-auto md:mr-0"
            src={featureInboxOne}
            alt="Billing and payments on Zoee, the all-in-one coaching platform"
          />
        </section>

        {/* Simplified Scheduling + Booking */}
        <section
          className="container my-36 grid items-center gap-12 md:my-24 md:grid-cols-2"
          id="simplified-scheduling-and-booking"
        >
          <div className="flex flex-col items-center gap-4 md:items-start">
            <h2 className="text-balance text-center text-[24px] font-bold text-black md:text-left md:text-[36px] lg:text-[42px]">
              Simplified Scheduling + Booking
            </h2>
            <p className="text-center text-sm md:text-left md:text-base lg:text-lg">
              Zoee simplifies your booking and scheduling process through joint
              calendar availability for easy booking and rescheduling, session
              reminders, with option to sync all of your calendars to increase
              bookings, prevent missed appointments and eliminate calendar
              chaos.
            </p>
          </div>

          <img
            className="mx-auto md:-order-1 md:ml-0 md:mr-auto"
            src={featureScheduling}
            alt="Simplified scheduling on Zoee, the all-in-one coaching platform"
          />
        </section>

        {/* Effortless Client Management */}
        <section
          className="container my-36 grid items-center gap-12 md:my-24 md:grid-cols-2"
          id="efforless-client-management"
        >
          <div className="flex flex-col items-center gap-4 md:items-start">
            <h2 className="text-balance text-center text-[24px] font-bold text-black md:text-left md:text-[36px] lg:text-[42px]">
              Effortless Client Management
            </h2>
            <p className="text-center text-sm md:text-left md:text-base lg:text-lg">
              Easily store and manage all of your contacts in one place with our
              all-in-one coaching software. Zoee organizes your prospects,
              client and group info, tracks client hours, invoice histories,
              program subscriptions and view session notes— all synced
              automatically within Zoee.
            </p>
          </div>

          <img
            className="mx-auto md:ml-auto md:mr-0"
            src={featurePayment}
            alt="Client management on Zoee, the all-in-one coaching software"
          />
        </section>

        {/* Client Communication Hub */}
        <section
          className="container my-36 grid items-center gap-12 md:my-24 md:grid-cols-2"
          id="client-communication-hub"
        >
          <div className="flex flex-col items-center gap-4 md:items-start">
            <h2 className="text-balance text-center text-[24px] font-bold text-black md:text-left md:text-[36px] lg:text-[42px]">
              Client Communication Hub
            </h2>
            <p className="text-center text-sm md:text-left md:text-base lg:text-lg">
              Streamline all your communications in one place so you can focus
              on connecting with your clients. Zoee enables seamless video
              sessions, chat messaging with notifications, calendar
              integrations, and digital note-taking–automatically saved for
              future reference.
            </p>
          </div>

          <img
            className="mx-auto md:-order-1 md:ml-0 md:mr-auto"
            src={featureCommunication}
            alt="Client communication on Zoee, the all-in-one coaching platform"
          />
        </section>

        {/* Client Portals */}
        <section
          className="container my-36 grid items-center gap-12 md:my-24 md:grid-cols-2"
          id="client-portals"
        >
          <div className="flex flex-col items-center gap-4 md:items-start">
            <h2 className="text-balance text-center text-[24px] font-bold text-black md:text-left md:text-[36px] lg:text-[42px]">
              Client Portals
            </h2>
            <p className="text-center text-sm md:text-left md:text-base lg:text-lg">
              Everything your client needs in one secure, centralized place. The
              portal allows your clients to communicate with you via chat and
              video sessions, share files, access their personal development
              journal and calendar, book sessions, view their invoices, and
              more.
            </p>
          </div>

          <img
            className="mx-auto md:ml-auto md:mr-0"
            src={clientPortals}
            alt="Client portals on Zoee, the all-in-one coaching software platform"
          />
        </section>

        <div className="relative z-20 m-auto">
          <PricingToggleSection />
        </div>

        <div className="gray-background-gradient text-grayCharcoal relative flex w-full flex-col items-center justify-center px-[24px] py-[180px] font-bold">
          <h6
            className="text-grayFlash absolute right-[-220px] top-[-680px] z-0 -rotate-90 text-center text-[111px] font-bold leading-[94px] md:right-[-314px] md:top-[-600px] md:text-[160px] md:leading-[150px]"
            aria-hidden="true"
          >
            DO LIFE <br />
            TOGETHER
          </h6>
          <img
            className="absolute top-[-72px] z-0 hidden md:left-[140px] md:block xl:left-[242px]"
            src={zoeeLogoOutline}
            alt="zoee logo outline"
          />
          <div>
            <h4 className="mb-[16px] text-[40px] sm:text-[48px]">
              Just picture it:
            </h4>
            <p className="mb-[16px] max-w-[608px] text-[18px]">
              You&apos;re sipping your morning coffee and looking at a full day
              of coaching sessions on the calendar, all seamlessly managed
              through your all-in-one coaching platform. Clients are messaging
              you with updates on their goals, you&apos;ve just gotten paid, and
              your dog is sleeping patiently at your feet.
            </p>
            <h5 className="mb-[32px] text-[36px] font-bold">
              What
              <span className="text-blurple">&nbsp;better feeling&nbsp;</span>
              is there?
            </h5>
            <Button
              onClick={() => showRegister({ accountType: "coach" })}
              size="lg"
            >
              Start Now
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

function PricingToggleSection() {
  const [isMonthly, setIsMonthly] = useState<boolean>(true)

  const { user } = useContext(UserCtx)
  const navigate = useNavigate()
  const { showRegister } = useAuthModal()
  const [clientNotification, setClientNotification] = useState(false)

  const handleStartNow = () => {
    if (user) {
      if (user.activeProfile === "coach") {
        navigate("/coach/subscription/plans", {
          state: { planType: isMonthly ? "monthly" : "annual" },
        })
      } else {
        setClientNotification(false)
        setTimeout(() => {
          setClientNotification(true)
        }, 0)
      }
    } else {
      showRegister({
        accountType: "coach",
        onSuccess: () => {
          navigate("/coach/subscription/plans", {
            state: { planType: isMonthly ? "monthly" : "annual" },
          })
        },
      })
    }
  }

  return (
    <section className="px-[24px] pb-[188px] md:pb-[212px]">
      {clientNotification && (
        <ClientNotification showNotification={clientNotification} />
      )}{" "}
      <div className="max-w-[862px] bg-white  mx-auto flex flex-col justify-center items-center py-[64px] rounded-[16px] border border-grayMist">
        <h3 className="font-bold text-[56px] text-grayCharcoal mb-[24px]">
          Pricing
        </h3>
        <div className="flex flex-col md:flex-row md:gap-[44px] gap-[8px] mb-[32px]">
          <div className="flex items-center gap-[10px]">
            <h4 className="font-bold text-[16px]">
              Annual
              <span className="text-green">&nbsp;(Save 16%)</span>
            </h4>
            <Switch
              defaultChecked={isMonthly}
              onCheckedChange={() => setIsMonthly((prev) => !prev)}
            />
            <h4 className="font-bold text-[16px]">Monthly</h4>
          </div>
          <div className="text-center">
            {isMonthly ? (
              <h3 className="font-bold text-[48px]">
                $49
                <span className="text-base text-graySlate font-normal">
                  /month
                </span>
              </h3>
            ) : (
              <h3 className="font-bold text-[48px]">
                $490
                <span className="text-base text-graySlate font-normal">
                  /year
                </span>
              </h3>
            )}
          </div>
        </div>
        <div className="max-w-[596px] bg-[#DEC8FB] bg-opacity-40 flex items-center gap-[20px] rounded-[16px] px-[24px] py-[20px] mx-[36px] mb-[32px]">
          <div className="flex items-center justify-center bg-white rounded-full h-[40px] w-[40px] min-w-[40px]">
            <SvgLightning />
          </div>
          <div className="flex flex-col justify-center gap-[4px]">
            <h5 className="font-bold">Let&apos;s Do Life Together</h5>
            <p className="gray-charcoal text-base">
              With a Zoee membership, you&apos;ll get back to coaching clients
              and away from juggling your back office.
            </p>
          </div>
        </div>
        <Button onClick={handleStartNow} size="lg">
          Start Now
        </Button>
      </div>
    </section>
  )
}
